import { useMemo } from 'react'

import { LineChartComponent, Text } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatDate, formatMs, getChartColor } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Grafico lineare dell'andamento temporale delle unit selezionate.
 * @param {{ startDate: Date, endDate: Date, selectedUnits: string[] }} props
 * @returns {JSX.Element|null}
 */
export const TimeSeriesChart = ({ startDate, endDate, selectedUnits = [] }) => {
  const { data: timeSeries = [], isSuccess } = useCQuery([
    'unitPerformanceTimeSeries',
    startDate,
    endDate,
    selectedUnits.join(',')
  ])

  const { chartData, dataKeys } = useMemo(() => {
    if (!timeSeries.length || !selectedUnits.length) {
      return { chartData: [], dataKeys: [] }
    }

    const bucketMap = {}
    for (const point of timeSeries) {
      if (!selectedUnits.includes(point.unit)) continue
      const bucketKey =
        typeof point.bucket === 'string' ? point.bucket : new Date(point.bucket).toISOString()

      if (!bucketMap[bucketKey]) {
        bucketMap[bucketKey] = { name: formatDate(bucketKey) }
      }
      bucketMap[bucketKey][point.unit] = point.avg
      bucketMap[bucketKey][`${point.unit}_errors`] = point.errorCount
    }

    const sorted = Object.entries(bucketMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v)

    const keys = selectedUnits.map((unit, i) => ({
      dataKey: unit,
      color: getChartColor(i)
    }))

    return { chartData: sorted, dataKeys: keys }
  }, [timeSeries, selectedUnits])

  if (!isSuccess) return null

  if (!selectedUnits.length) {
    return (
      <div className="ncard">
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-chart-line" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.pfAndamentoNelTempo}</h3>
            <div className="ncard__sub">{t.pfSelezionaUnitTabella}</div>
          </div>
        </div>
        <div
          className="ncard__body"
          style={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-400)'
          }}
        >
          {t.pfSelezionaUnaPiuUnit}
        </div>
      </div>
    )
  }

  return (
    <div className="ncard">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-line" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfAndamentoNelTempo}</h3>
          <div className="ncard__sub">
            {selectedUnits.length} {t.pfUnitSelezionate} · max 5
          </div>
        </div>
        <div className="ncard__head-actions">
          {selectedUnits.map((unit, i) => (
            <span
              key={unit}
              className="nchip"
              style={{
                background: i === 0 ? 'var(--primary-tint)' : 'var(--surface-3)',
                color: 'var(--ink-700)'
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: getChartColor(i)
                }}
              />
              {unit}
            </span>
          ))}
        </div>
      </div>
      <div className="ncard__body">
        <div style={{ height: 350 }}>
          {chartData.length > 0 ? (
            <LineChartComponent
              data={chartData}
              dataKeys={dataKeys}
              xaxis
              yaxis
              cartesian
              xAxisProps={{ dataKey: 'name', tick: { fontSize: 10 } }}
              yAxisProps={{
                tickFormatter: (val) => formatMs(val, true)
              }}
              tooltipProps={{
                formatter: (value, name) => [formatMs(value), name],
                labelStyle: { fontWeight: 'bold' }
              }}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-400)'
              }}
            >
              <Text value={t.pfNessunDatoNelPeriodoSelezionato} size={14} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
